import React from 'react'
import { useTranslation } from 'react-i18next'
import { File as FileIcon, UploadCloud, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Editor } from '@/components/ui/editor'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { DownloadFileLimits } from '@/types/downloads'
import { DownloadFileFormats } from './DownloadFileFormats'
import { progressText } from './downloadFormatText'
import { fileAccept } from './downloadUtils'
import { titleFromFileName } from './downloadsBulkQueue'
import type { UploadProgress } from './downloadUpload'
import type { DownloadSingleDraft, DownloadSingleErrors } from './downloadSingle'

/**
 * The step of a one-file space (F-75): the title, the description and the file. The item is
 * created right after the space, the file goes up with progress, and the person lands on it to
 * add the cover and publish. Price is the item's product, set later as for any download.
 */
export const DownloadSingleStep: React.FC<{
  draft: DownloadSingleDraft
  onChange: (draft: DownloadSingleDraft) => void
  errors?: DownloadSingleErrors | null
  /** The upload in flight, if any. */
  upload?: UploadProgress | null
  /** The file is already on the item (a retry only fixes the rest). */
  uploaded?: boolean
  limits?: DownloadFileLimits | null
  disabled?: boolean
}> = ({ draft, onChange, errors, upload, uploaded = false, limits, disabled = false }) => {
  const { t, i18n } = useTranslation(['downloads', 'common'])
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = React.useState(false)
  /* The rich editor keeps the callback it was born with: it reads the latest draft through this. */
  const latest = React.useRef({ draft, onChange })
  latest.current = { draft, onChange }

  /* The file name is the starting title, until the person writes their own. */
  const pick = (file: File | undefined) => {
    if (!file) return
    const previous = draft.file ? titleFromFileName(draft.file.name) : ''
    const keepTitle = draft.title.trim() && draft.title !== previous
    onChange({ ...draft, file, title: keepTitle ? draft.title : titleFromFileName(file.name) })
  }
  const locked = disabled || uploaded

  return (
    <div className="min-w-0 space-y-5" data-testid="download-single-step">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('downloads:wizard.step')}</h3>
        <p className="text-xs text-gray-500">{t('downloads:wizard.stepHint')}</p>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-gray-700">{t('downloads:wizard.file')} <span className="text-red-500">*</span></span>
        <input ref={inputRef} type="file" accept={fileAccept(limits)} className="hidden" data-testid="download-single-input"
          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; pick(file) }} />
        {draft.file ? (
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex min-w-0 items-center gap-3">
              <FileIcon className="h-5 w-5 flex-shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-900" data-testid="download-single-file">{draft.file.name}</span>
              {!locked && (
                <>
                  <Button type="button" variant="outline" size="sm" className="flex-shrink-0" onClick={() => inputRef.current?.click()}>
                    {t('downloads:wizard.change')}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0" aria-label={t('downloads:wizard.remove')}
                    onClick={() => onChange({ ...draft, file: null })}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
            {(upload || uploaded) && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-gray-500">{uploaded ? t('downloads:wizard.uploaded') : progressText(t as never, i18n.language, upload!)}</p>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-100" role="progressbar"
                  aria-valuenow={uploaded ? 100 : upload!.percent} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full bg-primary transition-all" style={{ width: `${uploaded ? 100 : upload!.percent}%` }} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); if (!locked) setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (!locked) pick(e.dataTransfer.files?.[0]) }}
            className={cn(
              'rounded-xl border-2 border-dashed p-6 text-center transition-colors',
              dragging ? 'border-primary bg-primary/5' : errors?.file ? 'border-red-300 bg-red-50/40' : 'border-gray-300 bg-gray-50'
            )}
          >
            <UploadCloud className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-900">{t('downloads:files.drop.titleSingle')}</p>
            <DownloadFileFormats limits={limits} className="mt-1" />
            <Button type="button" variant="outline" size="sm" className="mt-3" disabled={locked} onClick={() => inputRef.current?.click()} data-testid="download-single-pick">
              {t('downloads:files.drop.pickSingle')}
            </Button>
          </div>
        )}
        {errors?.file && <p role="alert" className="text-xs text-red-600 [overflow-wrap:anywhere]">{errors.file}</p>}
      </div>

      <div className="grid gap-2">
        <label htmlFor="single-download-title" className="text-sm font-medium text-gray-700">
          {t('downloads:form.fields.title')} <span className="text-red-500">*</span>
        </label>
        <Input id="single-download-title" value={draft.title} disabled={disabled} maxLength={140} aria-invalid={Boolean(errors?.title)}
          onChange={(e) => onChange({ ...draft, title: e.target.value })} placeholder={t('downloads:form.fields.titlePlaceholder')} />
        {errors?.title && <p className="text-xs text-red-600">{errors.title}</p>}
      </div>

      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">{t('downloads:form.fields.description')}</label>
        <div className={disabled ? 'pointer-events-none opacity-60' : undefined}>
          <Editor aiFieldKind="download" content={draft.description}
            onChange={(description) => latest.current.onChange({ ...latest.current.draft, description })} className="min-h-[110px] sm:min-h-[140px]" />
        </div>
      </div>

      <p className="text-xs text-gray-500">{t('downloads:wizard.bornDraft')}</p>
    </div>
  )
}
